import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { LayoutGroup, motion, useReducedMotion } from 'framer-motion';
import { getBotInviteUrl, trackUserServerOpen } from '../lib/api';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { useI18n } from '../contexts/I18nContext';
import { useAuth } from '../contexts/AuthContext';
import { useAccessibleGuilds } from '../contexts/AccessibleGuildsContext';
import { LogOut, Menu, X, Zap, User } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from './ui/sonner';
import { buildDashboardPath, buildLocaleRootPath, buildServerPath, normalizeLang, parseAppPath } from '../lib/routes';
import { dispatchGuildSelection } from '../lib/guildSelection';

const getGuildIconUrl = (guildId, iconHash) => {
  if (!guildId || !iconHash) return '';
  const isAnimated = String(iconHash).startsWith('a_');
  const ext = isAnimated ? 'gif' : 'png';
  return `https://cdn.discordapp.com/icons/${guildId}/${iconHash}.${ext}?size=96`;
};

const getAvatarUrl = (userId, avatarHash, size = 96) => {
  if (!userId || !avatarHash) return '';
  const isAnimated = String(avatarHash).startsWith('a_');
  const ext = isAnimated ? 'gif' : 'png';
  return `https://cdn.discordapp.com/avatars/${userId}/${avatarHash}.${ext}?size=${size}`;
};

const getInitials = (name) => {
  const parts = String(name || '').split(' ').filter(Boolean);
  const a = parts[0]?.[0] || 'S';
  const b = parts[1]?.[0] || '';
  return (a + b).toUpperCase();
};

export const GuildBar = ({ sidebarCollapsed, sidebarLocked = false, onToggleSidebar }) => {
  const animationGroup = useId();
  const reduced = useReducedMotion();
  const { t, language } = useI18n();
  const { user, login, logout } = useAuth();
  const { guilds, loading, error, refresh } = useAccessibleGuilds();
  const navigate = useNavigate();
  const location = useLocation();
  const isRtl = language === 'ar';
  const pathState = useMemo(() => parseAppPath(location.pathname), [location.pathname]);
  const routeLang = normalizeLang(pathState.lang || language || 'ar');
  const currentServerSubPath =
    pathState.scope === 'server' ? (pathState.subPath || 'server-overview') : 'server-overview';
  const [activeGuildId, setActiveGuildId] = useState('');
  const lastSetupGuildIdRef = useRef('');
  const userId = user?.user_id || '';

  // Load selected guild from localStorage
  useEffect(() => {
    const savedGuildId = localStorage.getItem('tns:selected_guild_id');
    if (savedGuildId) {
      setActiveGuildId(savedGuildId);
    }
  }, []);

  // Sync selection when other parts of the app emit changes
  useEffect(() => {
    const handler = (event) => {
      const g = event?.detail?.guild || null;
      const gid = (g?.id || '').toString();
      if (!gid) {
        localStorage.removeItem('tns:selected_guild_id');
        localStorage.removeItem('tns:selected_guild_access');
        localStorage.removeItem('tns:selected_guild_scopes');
      }
      setActiveGuildId((prev) => (prev === gid ? prev : gid));
    };
    window.addEventListener('tns:guild-selected', handler);
    return () => window.removeEventListener('tns:guild-selected', handler);
  }, []);

  // Clear selection when navigating to /profile
  useEffect(() => {
    if (pathState.scope === 'dashboard') {
      setActiveGuildId('');
      localStorage.removeItem('tns:selected_guild_id');
      localStorage.removeItem('tns:selected_guild_access');
      localStorage.removeItem('tns:selected_guild_scopes');
      dispatchGuildSelection(null);
    }
  }, [pathState.scope]);

  useEffect(() => {
    if (pathState.scope !== 'server' || !pathState.guildId) return;
    const gid = String(pathState.guildId);
    setActiveGuildId((prev) => (prev === gid ? prev : gid));
    localStorage.setItem('tns:selected_guild_id', gid);
  }, [pathState.guildId, pathState.scope]);

  const tooltipSide = language === 'ar' ? 'left' : 'right';
  const indicatorEdgeClass = language === 'ar' ? '-right-2 rounded-l-full' : '-left-2 rounded-r-full';
  const dividerEdgeClass = language === 'ar' ? 'border-l' : 'border-r';

  useEffect(() => {
    if (error?.response?.status !== 401) return;
    const alreadyTried = sessionStorage.getItem('tns:auto-relogin') === '1';
    if (alreadyTried) return;
    sessionStorage.setItem('tns:auto-relogin', '1');
    const timer = setTimeout(async () => {
      await logout();
      await login();
    }, 50);
    return () => clearTimeout(timer);
  }, [error, login, logout]);

  const load = useCallback(() => {
    refresh({ force: true }).catch(() => {});
  }, [refresh]);

  const sortedGuilds = useMemo(() => guilds || [], [guilds]);
  const currentSelectedGuild = useMemo(() => {
    const gid = String(activeGuildId || '');
    if (!gid) return null;
    return (sortedGuilds || []).find((g) => String(g?.id || '') === gid) || null;
  }, [activeGuildId, sortedGuilds]);

  useEffect(() => {
    if (!currentSelectedGuild) return;
    dispatchGuildSelection(currentSelectedGuild);
  }, [currentSelectedGuild]);

  const emitSetup = useCallback(async (guild) => {
    const gid = guild?.id || '';
    let url = '';
    try {
      const data = await getBotInviteUrl(gid);
      url = data?.url || '';
    } catch (e) {
      url = '';
    }
    window.dispatchEvent(new CustomEvent('tns:guild-setup', { detail: { guild, inviteUrl: url } }));
  }, []);

  useEffect(() => {
    const run = async () => {
      if (!currentSelectedGuild) {
        lastSetupGuildIdRef.current = '';
        window.dispatchEvent(new CustomEvent('tns:guild-setup', { detail: null }));
        return;
      }
      if (currentSelectedGuild?.bot_in_guild !== false) {
        lastSetupGuildIdRef.current = '';
        window.dispatchEvent(new CustomEvent('tns:guild-setup', { detail: null }));
        return;
      }
      if (lastSetupGuildIdRef.current === currentSelectedGuild.id) return;
      lastSetupGuildIdRef.current = currentSelectedGuild.id;
      await emitSetup(currentSelectedGuild);
    };
    run();
  }, [currentSelectedGuild, emitSetup]);

  const handleClickGuild = useCallback(
    async (guild) => {
      const gid = guild?.id || '';
      if (!gid) return;
      
      // Save to localStorage
      setActiveGuildId(gid);
      localStorage.setItem('tns:selected_guild_id', gid);
      localStorage.setItem('tns:selected_guild_access', JSON.stringify(guild?.access_type || []));
      localStorage.setItem('tns:selected_guild_scopes', JSON.stringify(guild?.dashboard_scopes || []));
      
      dispatchGuildSelection(guild);
      if (userId) {
        trackUserServerOpen(userId, gid).catch(() => {});
      }
      
      if (!guild?.bot_in_guild) {
        await emitSetup(guild);
        navigate(buildServerPath(routeLang, gid, currentServerSubPath));
        return;
      }
      lastSetupGuildIdRef.current = '';
      window.dispatchEvent(new CustomEvent('tns:guild-setup', { detail: null }));
      
      // Navigate to server overview
      navigate(buildServerPath(routeLang, gid, currentServerSubPath));
    },
    [currentServerSubPath, emitSetup, navigate, routeLang, userId]
  );

  const handleToggleSidebar = useCallback(() => {
    if (sidebarLocked) {
      toast.info(
        t(
          'لا يمكن فتح الشريط الجانبي حتى دعوة البوت لهذا السيرفر أو اختيار سيرفر صالح.',
          'Sidebar is locked until the bot is invited to this server or you switch to a valid server.',
        ),
      );
      return;
    }
    onToggleSidebar?.();
  }, [onToggleSidebar, sidebarLocked, t]);

  return (
    <LayoutGroup id={animationGroup}>
      <TooltipProvider delayDuration={120}>
        <aside className={`tns-guild-bar w-[76px] shrink-0 bg-background ${dividerEdgeClass} border-border flex flex-col items-center pt-0 pb-3`}>
          <div className="h-16 w-full flex items-center justify-center px-2">
            <div
              className={`w-full flex items-center justify-center transition-all duration-200 ease-out transform-gpu ${
                sidebarCollapsed ? 'opacity-100 translate-y-0' : 'opacity-100 translate-y-0'
              }`}
            >
              {isRtl ? (
                <>
                  <button
                    type="button"
                    onClick={handleToggleSidebar}
                    className={`h-10 w-10 inline-flex items-center justify-center rounded-lg border text-foreground transition-colors ${
                      sidebarLocked
                        ? 'border-border/50 bg-secondary/20 cursor-not-allowed opacity-55'
                        : 'border-border bg-secondary/30 hover:bg-secondary/60'
                    }`}
                    aria-label={sidebarCollapsed ? t('فتح الشريط الجانبي', 'Open sidebar') : t('إغلاق الشريط الجانبي', 'Close sidebar')}
                    aria-expanded={!sidebarCollapsed}
                    aria-disabled={sidebarLocked}
                    data-testid="guildbar-toggle-sidebar"
                  >
                    {sidebarCollapsed ? <Menu className="h-6 w-6" /> : <X className="h-6 w-6" />}
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={handleToggleSidebar}
                    className={`h-10 w-10 inline-flex items-center justify-center rounded-lg border text-foreground transition-colors ${
                      sidebarLocked
                        ? 'border-border/50 bg-secondary/20 cursor-not-allowed opacity-55'
                        : 'border-border bg-secondary/30 hover:bg-secondary/60'
                    }`}
                    aria-label={sidebarCollapsed ? t('فتح الشريط الجانبي', 'Open sidebar') : t('إغلاق الشريط الجانبي', 'Close sidebar')}
                    aria-expanded={!sidebarCollapsed}
                    aria-disabled={sidebarLocked}
                    data-testid="guildbar-toggle-sidebar"
                  >
                    {sidebarCollapsed ? <Menu className="h-6 w-6" /> : <X className="h-6 w-6" />}
                  </button>
                </>
              )}
            </div>
          </div>

          <motion.div layoutScroll className="tns-guild-list flex-1 w-full overflow-y-auto overflow-x-hidden px-2 space-y-3">
            {loading ? (
              <div className="space-y-2">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="h-12 w-12 mx-auto rounded-3xl bg-secondary/50 animate-pulse" />
                ))}
              </div>
            ) : sortedGuilds.length ? (
              <>
                <div className="w-10 mx-auto h-px bg-border/80 mb-2" />
                {sortedGuilds.map((g) => {
                const isSelected = String(g.id) === String(activeGuildId);
                const iconUrl = getGuildIconUrl(g.id, g.icon);
                const initials = getInitials(g.name);
                return (
                  <Tooltip key={g.id}>
                    <TooltipTrigger asChild>
                      <motion.button
                        type="button"
                        onClick={() => handleClickGuild(g)}
                        data-dashboard-navigation={!isSelected || undefined}
                        className="tns-guild-button group relative w-full flex items-center justify-center"
                        aria-label={g.name}
                        aria-pressed={isSelected}
                        whileHover={reduced ? undefined : { y: -2 }}
                        whileTap={reduced ? undefined : { scale: 0.93 }}
                        transition={{ type: 'spring', stiffness: 400, damping: 28 }}
                        data-testid={`guildbar-guild-${g.id}`}
                      >
                        {isSelected && <motion.span layoutId="active-guild" aria-hidden="true"
                          className={`tns-guild-indicator absolute z-10 ${indicatorEdgeClass} top-1 w-[3px] h-10 bg-primary`}
                          transition={reduced ? { duration: 0 } : { type: 'spring', stiffness: 340, damping: 30 }} />}
                        <motion.span
                          initial={false}
                          animate={{ borderRadius: isSelected ? 14 : 24, scale: isSelected && !reduced ? 1.04 : 1 }}
                          transition={{ type: 'spring', stiffness: 340, damping: 26 }}
                          className={`tns-guild-avatar relative h-12 w-12 overflow-hidden flex items-center justify-center ${isSelected ? 'bg-primary/15 text-primary' : 'bg-secondary text-foreground'}`}
                        >
                          {iconUrl ? (
                            <img
                              src={iconUrl}
                              alt={g.name}
                              className="h-full w-full object-cover"
                              draggable={false}
                            />
                          ) : (
                            <span className="font-heading font-semibold text-sm">{initials}</span>
                          )}
                          <span
                            className={`absolute bottom-1 right-1 h-2 w-2 rounded-full border border-background ${
                              g.bot_in_guild ? 'bg-emerald-500' : 'bg-muted-foreground'
                            }`}
                          />
                        </motion.span>
                      </motion.button>
                    </TooltipTrigger>
                    <TooltipContent side={tooltipSide} sideOffset={14} className="bg-card text-foreground border border-border shadow-lg">
                      <div className="font-medium">{g.name}</div>
                      {!g.bot_in_guild ? (
                        <div className="text-[11px] text-muted-foreground mt-0.5">
                          {t('البوت غير موجود', 'Bot not installed')}
                        </div>
                      ) : null}
                    </TooltipContent>
                  </Tooltip>
                );
                })}
              </>
            ) : (
              <div className="h-full flex items-center justify-center px-2">
                <button
                  type="button"
                  onClick={load}
                  className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                  data-testid="guildbar-retry-btn"
                >
                  {t('إعادة المحاولة', 'Retry')}
                </button>
              </div>
            )}
          </motion.div>

          <div
            className={`w-full px-2 transition-all duration-200 ease-out transform-gpu ${
              sidebarCollapsed ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-1 pointer-events-none'
            }`}
          >
            <div className="w-10 mx-auto h-px bg-border/80 my-2" />
            
            {/* User Profile Button */}
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={() => {
                    // Clear selected guild and navigate to profile
                    setActiveGuildId('');
                    localStorage.removeItem('tns:selected_guild_id');
                    localStorage.removeItem('tns:selected_guild_access');
                    localStorage.removeItem('tns:selected_guild_scopes');
                    // Dispatch event to notify Sidebar and other components
                    dispatchGuildSelection(null);
                    navigate(buildDashboardPath(routeLang, 'profile'));
                  }}
                  className="group relative w-full flex items-center justify-center mb-2"
                  aria-label={t('صفحة المستخدم', 'User Profile')}
                  data-testid="guildbar-user-profile"
                  data-dashboard-navigation
                >
                  <span
                    className={`relative h-12 w-12 overflow-hidden flex items-center justify-center transition-[border-radius,transform,background-color] duration-200 rounded-3xl group-hover:rounded-2xl group-hover:scale-[1.04] ${
                      !activeGuildId ? 'ring-2 ring-primary ring-offset-2 ring-offset-background' : ''
                    }`}
                  >
                    {user?.avatar ? (
                      <img
                        src={getAvatarUrl(user.user_id, user.avatar, 96)}
                        alt={user.username || 'User'}
                        className="h-full w-full object-cover"
                        draggable={false}
                      />
                    ) : (
                      <span className="h-full w-full bg-secondary flex items-center justify-center font-heading font-semibold text-sm">
                        {(user?.username?.[0] || 'U').toUpperCase()}
                      </span>
                    )}
                  </span>
                </button>
              </TooltipTrigger>
              <TooltipContent side={tooltipSide} sideOffset={14} className="bg-card text-foreground border border-border shadow-lg">
                <div className="font-medium">{t('صفحة المستخدم', 'User Profile')}</div>
              </TooltipContent>
            </Tooltip>
            
            {/* Logout Button */}
            <button
              type="button"
              onClick={async () => {
                await logout();
                navigate(buildLocaleRootPath(routeLang), { replace: true });
              }}
              className="group relative w-full flex items-center justify-center"
              aria-label={t('تسجيل الخروج', 'Logout')}
              data-testid="guildbar-logout"
              data-dashboard-navigation
            >
              <span className="relative h-12 w-12 overflow-hidden flex items-center justify-center bg-secondary text-foreground transition-[border-radius,transform,background-color] duration-200 rounded-3xl group-hover:rounded-2xl group-hover:scale-[1.04]">
                <LogOut className="h-5 w-5 text-destructive" />
              </span>
            </button>
          </div>
        </aside>
      </TooltipProvider>
    </LayoutGroup>
  );
};
